import AnimatedEllipsis from 'components/utilities/animated-ellipsis'
import classNames from 'helpers/class-names'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import {
  Dispatch,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  RefObject,
  forwardRef,
  useState,
} from 'react'

const Ripple = dynamic(() => import('components/utilities/ripple'), {
  ssr: false,
})

interface Props extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  children: ReactNode
  primary?: boolean
  danger?: boolean
  secondary?: boolean
  disabled?: boolean
  ripple?: boolean
  isLoading?: boolean
  onClick?: Dispatch<MouseEvent>
  type?: 'button' | 'submit' | 'reset'
  className?: string
  rippleClassName?: string
  asAnchor?: boolean
  asLink?: boolean
  href?: string
  form?: string
  target?: string
}

const Button = forwardRef(
  (
    {
      primary = false,
      danger = false,
      secondary = false,
      disabled = false,
      ripple = true,
      type = 'button',
      className,
      rippleClassName,
      isLoading = false,
      children,
      onClick,
      asAnchor = false,
      asLink = false,
      href = null,
      form = null,
      ...props
    }: Props,
    ref: RefObject<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (primary && secondary) {
      throw new Error('Primary and secondary can not both be true.')
    }

    const buttonClasses = classNames(
      'bg-gray border-gray text-white active:bg-gray-600 hover:bg-gray-600',
      'font-bold text-md py-2 px-10 border rounded-full cursor-pointer select-none',
      'active:translate-y-0.5 focus:outline-none focus-visible:ring-2',
      'focus-visible:ring-info/75 tracking-wide',
      'focus-visible:border-info focus-visible:border transition duration-500',
      {
        'bg-primary border-primary active:bg-primary-darken hover:bg-primary-darken':
          primary && !disabled,
        'bg-danger border-danger active:bg-danger-darken hover:bg-danger-darken':
          danger && !disabled,
        'bg-gray-200 dark:bg-gray-800 border-gray-200 dark:border-gray-800 text-gray-50 dark:text-gray-600 cursor-not-allowed active:bg-gray-200 dark:active:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800':
          disabled,
        'bg-white text-gray-400 border-gray-200 active:bg-gray-50 hover:bg-gray-50 dark:bg-gray-500 dark:border-gray-700 dark:text-gray-100':
          secondary && !disabled,
        'bg-transparent text-gray-200 border-gray-100 active:bg-transparent hover:bg-transparent dark:bg-transparent dark:border-gray-500 dark:text-gray-500 dark:active:bg-transparent dark:hover:bg-transparent':
          secondary && disabled,
        'relative overflow-hidden': ripple,
        'block text-center': asAnchor || asLink,
      },
      className,
    )

    const rippleClasses = classNames(
      { 'bg-gray-100': secondary && !disabled },
      rippleClassName,
    )
    const [rippleElement, setRippleElement] = useState<ReactNode>(null)

    const clickHandler = (event: React.MouseEvent): void => {
      if (ripple) {
        setRippleElement(
          <Ripple
            element={event.target as HTMLElement}
            x={event.clientX}
            y={event.clientY}
            className={rippleClasses}
          />,
        )
      }

      if (typeof onClick === 'function') {
        if (!asAnchor && !asLink) {
          event.preventDefault()
        }

        onClick(event)
      }
    }

    /**
     * children need to be in a div so that when the ripple element is rendered, the button still respects the
     * children's width, even when its just a text node
     */
    const content = !isLoading ? (
      <div className="mx-auto flex w-max flex-row">{children}</div>
    ) : (
      <AnimatedEllipsis />
    )

    if (asAnchor || asLink) {
      if (href && href.charAt(0) === '/') {
        const anchor = (
          <a
            href={asLink ? null : disabled ? '#' : href}
            aria-disabled={disabled}
            onClick={clickHandler}
            ref={ref as RefObject<HTMLAnchorElement>}
            className={buttonClasses}
            {...props}>
            {content}
            {rippleElement}
          </a>
        )
        return asLink ? (
          <Link passHref href={disabled ? '#' : href}>
            {anchor}
          </Link>
        ) : (
          anchor
        )
      }

      return (
        <a
          href={href}
          onClick={clickHandler}
          ref={ref as RefObject<HTMLAnchorElement>}
          className={buttonClasses}
          {...props}>
          {content}
          {rippleElement}
        </a>
      )
    }

    return (
      <button
        aria-label={isLoading ? 'Loading...' : ''}
        type={type}
        form={type === 'submit' ? form : null}
        ref={ref as RefObject<HTMLButtonElement>}
        onClick={clickHandler}
        className={buttonClasses}
        {...props}
        disabled={disabled}>
        {content}
        {rippleElement}
      </button>
    )
  },
)

export default Button
